import { Menu as MenuPrimitive } from "@base-ui/react/menu";
import { Spinner } from "@timeghost/ui/shadcn/spinner";
import { cn } from "@timeghost/ui/utils";
import { cva } from "class-variance-authority";
import { CheckIcon, ChevronRightIcon } from "lucide-react";
import type * as React from "react";

function DropdownMenu({ ...props }: MenuPrimitive.Root.Props) {
	return <MenuPrimitive.Root data-slot="dropdown-menu" {...props} />;
}

function DropdownMenuPortal({ ...props }: MenuPrimitive.Portal.Props) {
	return <MenuPrimitive.Portal data-slot="dropdown-menu-portal" {...props} />;
}

function DropdownMenuTrigger({ ...props }: MenuPrimitive.Trigger.Props) {
	return <MenuPrimitive.Trigger data-slot="dropdown-menu-trigger" {...props} />;
}

function DropdownMenuContent({
	align = "start",
	alignOffset = 0,
	side = "bottom",
	sideOffset = 4,
	className,
	...props
}: MenuPrimitive.Popup.Props & Pick<MenuPrimitive.Positioner.Props, "align" | "alignOffset" | "side" | "sideOffset">) {
	return (
		<MenuPrimitive.Portal>
			<MenuPrimitive.Positioner
				align={align}
				alignOffset={alignOffset}
				className="isolate z-50 outline-none"
				side={side}
				sideOffset={sideOffset}>
				<MenuPrimitive.Popup
					className={cn(
						"data-[side=bottom]:slide-in-from-top-1 data-[side=inline-end]:slide-in-from-left-1 data-[side=inline-start]:slide-in-from-right-1 data-[side=left]:slide-in-from-right-1 data-[side=right]:slide-in-from-left-1 data-[side=top]:slide-in-from-bottom-1 data-open:fade-in-0 data-open:zoom-in-95 data-closed:fade-out-0 data-closed:zoom-out-95 relative z-50 max-h-(--available-height) w-(--anchor-width) min-w-48 origin-(--transform-origin) overflow-y-auto overflow-x-hidden rounded-xl bg-white/85 p-1 text-popover-foreground shadow-lg outline-none ring-1 ring-foreground/10 backdrop-blur-lg duration-100 ease-out before:pointer-events-none before:absolute before:inset-0 before:-z-1 before:rounded-[inherit] before:backdrop-blur-2xl before:backdrop-saturate-150 data-closed:animate-out data-open:animate-in data-closed:overflow-hidden **:data-[slot$=-item]:data-highlighted:bg-glass-hover **:data-[slot$=-separator]:bg-border **:data-[slot$=-trigger]:aria-expanded:bg-glass-hover! **:data-[slot$=-item]:focus:bg-glass-hover **:data-[slot$=-trigger]:focus:bg-glass-hover dark:bg-black/50",
						className
					)}
					data-slot="dropdown-menu-content"
					{...props}
				/>
			</MenuPrimitive.Positioner>
		</MenuPrimitive.Portal>
	);
}

function DropdownMenuGroup({ ...props }: MenuPrimitive.Group.Props) {
	return <MenuPrimitive.Group data-slot="dropdown-menu-group" {...props} />;
}

function DropdownMenuLabel({
	className,
	inset,
	...props
}: MenuPrimitive.GroupLabel.Props & {
	inset?: boolean;
}) {
	return (
		<MenuPrimitive.GroupLabel
			className={cn("px-2 py-1.5 font-medium text-muted-foreground text-xs data-inset:pl-7", className)}
			data-inset={inset}
			data-slot="dropdown-menu-label"
			{...props}
		/>
	);
}

const dropdownMenuItemVariants = cva(
	"group/dropdown-menu-item relative flex cursor-default select-none outline-hidden transition-colors duration-100 ease-out data-disabled:pointer-events-none data-disabled:opacity-50 [&_svg:not([class*='size-'])]:size-3.5 [&_svg]:pointer-events-none [&_svg]:shrink-0",
	{
		variants: {
			itemStyle: {
				default:
					"h-8 items-center gap-2 rounded-lg px-2 font-medium text-sm focus:bg-glass-hover focus:text-accent-foreground not-data-[variant=destructive]:focus:**:text-accent-foreground data-inset:pl-7 data-[variant=destructive]:text-destructive data-[variant=destructive]:focus:bg-destructive/10 data-[variant=destructive]:focus:text-destructive dark:data-[variant=destructive]:focus:bg-destructive/20 data-[variant=destructive]:*:[svg]:text-destructive",
				radio:
					"min-h-16 flex-col items-center justify-center gap-1 rounded-lg px-1 py-2 text-muted-foreground text-xs focus:bg-glass-hover focus:text-accent-foreground data-highlighted:bg-glass-hover data-highlighted:text-accent-foreground",
			},
			active: {
				true: "bg-glass-hover text-accent-foreground ring-1 ring-ring/40",
				false: "",
			},
		},
		defaultVariants: {
			itemStyle: "default",
			active: false,
		},
	}
);

function DropdownMenuItem({
	className,
	inset,
	itemStyle = "default",
	active = false,
	variant = "default",
	icon,
	loading = false,
	disabled,
	closeOnClick,
	children,
	...props
}: MenuPrimitive.Item.Props & {
	inset?: boolean;
	itemStyle?: "default" | "radio";
	active?: boolean;
	variant?: "default" | "destructive";
	icon?: React.ReactNode;
	loading?: boolean;
}) {
	const isDisabled = Boolean(disabled || loading);
	const leading = loading ? <Spinner color="current" size="xs" /> : icon;

	return (
		<MenuPrimitive.Item
			className={cn(dropdownMenuItemVariants({ itemStyle, active }), className)}
			data-inset={inset}
			data-loading={loading ? "true" : undefined}
			data-slot="dropdown-menu-item"
			data-variant={variant}
			{...props}
			aria-busy={loading || undefined}
			closeOnClick={loading ? false : closeOnClick}
			disabled={isDisabled}>
			{leading}
			{children}
		</MenuPrimitive.Item>
	);
}

function DropdownMenuSub({ ...props }: MenuPrimitive.SubmenuRoot.Props) {
	return <MenuPrimitive.SubmenuRoot data-slot="dropdown-menu-sub" {...props} />;
}

function DropdownMenuSubTrigger({
	className,
	inset,
	children,
	...props
}: MenuPrimitive.SubmenuTrigger.Props & {
	inset?: boolean;
}) {
	return (
		<MenuPrimitive.SubmenuTrigger
			className={cn(
				"flex h-8 cursor-default select-none items-center gap-2 rounded-lg px-2 font-medium text-sm outline-hidden transition-colors duration-100 ease-out focus:bg-glass-hover focus:text-accent-foreground not-data-[variant=destructive]:focus:**:text-accent-foreground data-open:bg-glass-hover data-popup-open:bg-glass-hover data-inset:pl-7 data-open:text-accent-foreground data-popup-open:text-accent-foreground [&_svg:not([class*='size-'])]:size-3.5 [&_svg]:pointer-events-none [&_svg]:shrink-0",
				className
			)}
			data-inset={inset}
			data-slot="dropdown-menu-sub-trigger"
			{...props}>
			{children}
			<ChevronRightIcon className="ml-auto size-3.5" />
		</MenuPrimitive.SubmenuTrigger>
	);
}

function DropdownMenuSubContent({
	align = "start",
	alignOffset = -3,
	side = "right",
	sideOffset = 0,
	className,
	...props
}: React.ComponentProps<typeof DropdownMenuContent>) {
	return (
		<DropdownMenuContent
			align={align}
			alignOffset={alignOffset}
			className={cn(
				"data-[side=bottom]:slide-in-from-top-1 data-[side=left]:slide-in-from-right-1 data-[side=right]:slide-in-from-left-1 data-[side=top]:slide-in-from-bottom-1 data-open:fade-in-0 data-open:zoom-in-95 data-closed:fade-out-0 data-closed:zoom-out-95 relative w-auto min-w-32 rounded-xl bg-white/90 dark:bg-black/55 p-1 text-popover-foreground shadow-lg ring-1 ring-foreground/10 duration-100 ease-out before:pointer-events-none before:absolute before:inset-0 before:-z-1 before:rounded-[inherit] before:backdrop-blur-2xl before:backdrop-saturate-150 data-closed:animate-out data-open:animate-in **:data-[slot$=-item]:data-highlighted:bg-glass-hover **:data-[slot$=-separator]:bg-border **:data-[slot$=-trigger]:aria-expanded:bg-glass-hover! **:data-[slot$=-item]:focus:bg-glass-hover **:data-[slot$=-trigger]:focus:bg-glass-hover",
				className
			)}
			data-slot="dropdown-menu-sub-content"
			side={side}
			sideOffset={sideOffset}
			{...props}
		/>
	);
}

function DropdownMenuCheckboxItem({
	className,
	children,
	checked,
	inset,
	...props
}: MenuPrimitive.CheckboxItem.Props & {
	inset?: boolean;
}) {
	return (
		<MenuPrimitive.CheckboxItem
			checked={checked}
			className={cn(
				"relative flex h-8 cursor-default select-none items-center gap-2 rounded-lg py-0 pr-8 pl-2 font-medium text-sm outline-hidden transition-colors duration-100 ease-out focus:bg-glass-hover focus:text-accent-foreground focus:**:text-accent-foreground data-disabled:pointer-events-none data-inset:pl-7 data-disabled:opacity-50 [&_svg:not([class*='size-'])]:size-3.5 [&_svg]:pointer-events-none [&_svg]:shrink-0",
				className
			)}
			data-inset={inset}
			data-slot="dropdown-menu-checkbox-item"
			{...props}>
			<span
				className="pointer-events-none absolute right-2 flex size-3.5 items-center justify-center"
				data-slot="dropdown-menu-checkbox-item-indicator">
				<MenuPrimitive.CheckboxItemIndicator>
					<CheckIcon className="size-3.5" />
				</MenuPrimitive.CheckboxItemIndicator>
			</span>
			{children}
		</MenuPrimitive.CheckboxItem>
	);
}

function DropdownMenuRadioGroup({ ...props }: MenuPrimitive.RadioGroup.Props) {
	return <MenuPrimitive.RadioGroup data-slot="dropdown-menu-radio-group" {...props} />;
}

function DropdownMenuRadioItem({
	className,
	children,
	inset,
	...props
}: MenuPrimitive.RadioItem.Props & {
	inset?: boolean;
}) {
	return (
		<MenuPrimitive.RadioItem
			className={cn(
				"relative flex h-8 cursor-default select-none items-center gap-2 rounded-lg py-0 pr-8 pl-2 font-medium text-sm outline-hidden transition-colors duration-100 ease-out focus:bg-glass-hover focus:text-accent-foreground focus:**:text-accent-foreground data-disabled:pointer-events-none data-inset:pl-7 data-disabled:opacity-50 [&_svg:not([class*='size-'])]:size-3.5 [&_svg]:pointer-events-none [&_svg]:shrink-0",
				className
			)}
			data-inset={inset}
			data-slot="dropdown-menu-radio-item"
			{...props}>
			<span
				className="pointer-events-none absolute right-2 flex size-3.5 items-center justify-center"
				data-slot="dropdown-menu-radio-item-indicator">
				<MenuPrimitive.RadioItemIndicator>
					<CheckIcon className="size-3.5" />
				</MenuPrimitive.RadioItemIndicator>
			</span>
			{children}
		</MenuPrimitive.RadioItem>
	);
}

function DropdownMenuSeparator({ className, ...props }: MenuPrimitive.Separator.Props) {
	return (
		<MenuPrimitive.Separator
			className={cn("-mx-1 my-1 h-px bg-border", className)}
			data-slot="dropdown-menu-separator"
			{...props}
		/>
	);
}

function DropdownMenuShortcut({ className, ...props }: React.ComponentProps<"span">) {
	return (
		<span
			className={cn(
				"ml-auto font-normal text-muted-foreground text-xs tracking-wide group-focus/dropdown-menu-item:text-accent-foreground",
				className
			)}
			data-slot="dropdown-menu-shortcut"
			{...props}
		/>
	);
}

export {
	DropdownMenu,
	DropdownMenuCheckboxItem,
	DropdownMenuContent,
	DropdownMenuGroup,
	DropdownMenuItem,
	DropdownMenuLabel,
	DropdownMenuPortal,
	DropdownMenuRadioGroup,
	DropdownMenuRadioItem,
	DropdownMenuSeparator,
	DropdownMenuShortcut,
	DropdownMenuSub,
	DropdownMenuSubContent,
	DropdownMenuSubTrigger,
	DropdownMenuTrigger,
};
